import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-8 px-3 py-1.5 has-[>svg]:px-2.5",
        icon: "size-8",
        // The 44px iOS minimum. Deliberately `default`'s padding with a taller
        // box, not `lg`'s: these raise a control's HEIGHT, and inheriting `lg`
        // would widen every call site as a side effect.
        "icon-touch": "size-11",
        lg: "h-9 rounded-md px-5 has-[>svg]:px-3.5",
        sm: "h-7 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2",
        // A control that must stay visually small uses `touch-target` instead.
        touch: "h-11 rounded-md px-3 has-[>svg]:px-2.5",
      },
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive dark:text-white dark:focus-visible:ring-destructive/40",
        ghost: "hover:bg-muted hover:text-foreground dark:hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
        outline:
          "border bg-background hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input dark:text-foreground dark:hover:bg-input/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      },
    },
  }
);

// asChild (Base UI has no Slot): when set, the single child element is rendered via
// useRender, which merges our className/props onto it. Otherwise a plain <button> is
// rendered.
function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  return useRender({
    defaultTagName: "button",
    props: {
      className: cn(buttonVariants({ className, size, variant })),
      "data-slot": "button",
      ...props,
      ...(asChild ? {} : { children }),
    },
    render:
      asChild && React.isValidElement(children)
        ? (children as React.ReactElement<Record<string, unknown>>)
        : undefined,
  });
}

export { Button, buttonVariants };
